import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { FaBars, FaTimes, FaShoppingCart, FaUser, FaCrown, FaGlobe } from 'react-icons/fa';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout, isAdmin } = useAuth();
  const { t } = useTranslation();
  const { currentLanguage, changeLanguage, isRTL } = useLanguage();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const toggleLanguage = () => {
    const newLang = currentLanguage === 'en' ? 'ar' : 'en';
    changeLanguage(newLang);
  };

  return (
    <nav className="bg-[#18100d] text-cigar-cream border-b border-cigar-brown/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 sm:space-x-4">
            <img 
              src="/cccigarnobg.png" 
              alt="CC Cigar Logo" 
              className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 object-contain"
            />
            <span className="text-lg sm:text-xl md:text-2xl font-cigar font-semibold">CC Cigar</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="hover:text-cigar-gold transition-colors">
              {t('navbar.home')}
            </Link>
            <Link to="/products" className="hover:text-cigar-gold transition-colors">
              {t('navbar.products')}
            </Link>
            <Link to="/cart" className="hover:text-cigar-gold transition-colors">
              <FaShoppingCart className={`inline ${isRTL ? 'ml-1' : 'mr-1'}`} />
              {t('navbar.cart')}
            </Link>
            
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-1 hover:text-cigar-gold transition-colors"
              title={currentLanguage === 'en' ? 'العربية' : 'English'}
            >
              <FaGlobe />
              <span className="text-sm">{currentLanguage === 'en' ? 'عربي' : 'EN'}</span>
            </button>
            
            {user ? (
              <div className="flex items-center space-x-4">
                {isAdmin() && (
                  <Link to="/admin" className="flex items-center space-x-1 text-cigar-gold hover:text-cigar-cream">
                    <FaCrown />
                    <span>{t('navbar.admin')}</span>
                  </Link>
                )}
                <div className="flex items-center space-x-2">
                  <FaUser />
                  <span>{t('navbar.welcome')}, {user.name}</span>
                </div>
                <button
                  onClick={handleLogout}
                  className="bg-cigar-brown hover:bg-cigar-light px-4 py-2 rounded transition-colors"
                >
                  {t('navbar.logout')}
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <Link to="/login" className="hover:text-cigar-gold transition-colors">
                  {t('navbar.login')}
                </Link>
                <Link to="/signup" className="bg-cigar-gold hover:bg-cigar-light text-[#18100d] px-4 py-2 rounded transition-colors">
                  {t('navbar.signup')}
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button and language toggle */}
          <div className="md:hidden flex items-center space-x-2">
            {/* Language Toggle for Mobile */}
            <button
              onClick={toggleLanguage}
              className="text-cigar-cream hover:text-cigar-gold focus:outline-none p-2"
              title={currentLanguage === 'en' ? 'العربية' : 'English'}
            >
              <FaGlobe size={18} />
            </button>
            
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-cigar-cream hover:text-cigar-gold focus:outline-none p-2"
              aria-label={t('navbar.menu')}
            >
              {isOpen ? <FaTimes size={20} /> : <FaBars size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-[#18100d] border-t border-cigar-brown/40">
              <Link
                to="/"
                className="block px-3 py-3 hover:text-cigar-gold transition-colors text-lg"
                onClick={() => setIsOpen(false)}
              >
                {t('navbar.home')}
              </Link>
              <Link
                to="/products"
                className="block px-3 py-3 hover:text-cigar-gold transition-colors text-lg"
                onClick={() => setIsOpen(false)}
              >
                {t('navbar.products')}
              </Link>
              <Link
                to="/cart"
                className="block px-3 py-3 hover:text-cigar-gold transition-colors text-lg"
                onClick={() => setIsOpen(false)}
              >
                {t('navbar.cart')}
              </Link>
              
              {user ? (
                <>
                  {isAdmin() && (
                    <Link
                      to="/admin"
                      className="block px-3 py-3 text-cigar-gold hover:text-cigar-cream transition-colors text-lg"
                      onClick={() => setIsOpen(false)}
                    >
                      {t('admin.dashboard')}
                    </Link>
                  )}
                  <div className="px-3 py-3 text-cigar-gold text-lg font-medium">
                    {t('navbar.welcome')}, {user.name}
                  </div>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsOpen(false);
                    }}
                    className="block w-full text-left px-3 py-3 hover:text-cigar-gold transition-colors text-lg"
                  >
                    {t('navbar.logout')}
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="block px-3 py-3 hover:text-cigar-gold transition-colors text-lg"
                    onClick={() => setIsOpen(false)}
                  >
                    {t('navbar.login')}
                  </Link>
                  <Link
                    to="/signup"
                    className="block px-3 py-3 hover:text-cigar-gold transition-colors text-lg"
                    onClick={() => setIsOpen(false)}
                  >
                    {t('navbar.signup')}
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
